import {
  nomeDaPeca,
  pecasImportadas,
  somarPorPeca,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import type { ResolvedServiceQuantity } from '@obraplan/core/domain/takeoff/service-quantity';
import type { TypologyDraft } from './typology-draft';

/**
 * A planilha por peça na conta da tipologia (Fase 28, RN-384).
 *
 * A peça importada traz a quantidade **pronta** — "B24 leva 33,18 kg de CA 50 - 8 MM" —, e o
 * desenho não a recalcula. Duas coisas decorrem disso, e moram aqui para que a lista de
 * itens, a consolidação, o orçamento e o resumo façam a mesma conta:
 *
 * - **a peça desenhada com o nome de uma importada sai da medida do desenho.** Hoje a peça
 *   importada não tem desenho (D3); no dia em que for posicionada (etapa 2), o bloco B24
 *   traçado mediria de novo o que a planilha já disse, e o concreto contaria duas vezes;
 * - **o número do serviço soma o das peças** ao que o desenho mede das outras — e perde para
 *   o que alguém digitou, como o item perde para o digitado (`QuantityOrigin` `PECAS`).
 */

/**
 * Como a tela chama a origem `PECAS` — a quantidade que veio pronta da planilha por peça. Ao
 * lado de "digitado", "calculado" e "planilha", na mesma caixa.
 */
export const ROTULO_DAS_PECAS = 'planilha por peça';

/** O que o rótulo quer dizer, para quem para o mouse em cima dele. */
export const EXPLICACAO_DAS_PECAS =
  'A quantidade veio pronta da planilha por peça: a soma das peças importadas, mais o que o ' +
  'desenho calcula das que não vieram nela. Não é índice, e o desenho não a recalcula. ' +
  'Digitar por cima vence, com o motivo.';

/** O que a tipologia guarda de peça — a desenhada e a importada. */
type PecasDaTipologia = Pick<
  TypologyDraft,
  | 'pieceQuantities'
  | 'pieces'
  | 'pileCaps'
  | 'groundBeams'
  | 'slabs'
  | 'retainingWalls'
  | 'retainingColumns'
  | 'stairs'
>;

/** A lista sem as peças de nome importado — e a mesma lista quando nada sai. */
function semAsImportadas<P extends { readonly name: string }>(
  lista: readonly P[],
  importadas: ReadonlySet<string>,
): readonly P[] {
  const ficam = lista.filter((peca) => !importadas.has(nomeDaPeca(peca.name)));
  return ficam.length === lista.length ? lista : ficam;
}

/**
 * A tipologia sem as peças desenhadas que vieram da planilha por peça (RN-384).
 *
 * Pelo **nome**, como a planilha as liga (ADR-178): "B24" importado tira o bloco B24 traçado
 * da conta, e só ele. Estaca, bloco, baldrame, radier e laje, muro e pilar de contenção e
 * escada — toda peça que a conta mede pelo nome dela. Sem peça importada, devolve a própria
 * tipologia: quem compara por identidade continua vendo "nada mudou".
 */
export function semPecasImportadas<T extends PecasDaTipologia>(typology: T): T {
  const importadas = pecasImportadas(typology.pieceQuantities);
  if (importadas.size === 0) return typology;
  const filtrar = <P extends { readonly name: string }>(lista: readonly P[] | undefined) =>
    lista === undefined ? undefined : semAsImportadas(lista, importadas);

  const pieces = filtrar(typology.pieces);
  const pileCaps = filtrar(typology.pileCaps);
  const groundBeams = filtrar(typology.groundBeams);
  const slabs = filtrar(typology.slabs);
  const retainingWalls = filtrar(typology.retainingWalls);
  const retainingColumns = filtrar(typology.retainingColumns);
  const stairs = filtrar(typology.stairs);
  return {
    ...typology,
    ...(pieces === undefined ? {} : { pieces }),
    ...(pileCaps === undefined ? {} : { pileCaps }),
    ...(groundBeams === undefined ? {} : { groundBeams }),
    ...(slabs === undefined ? {} : { slabs }),
    ...(retainingWalls === undefined ? {} : { retainingWalls }),
    ...(retainingColumns === undefined ? {} : { retainingColumns }),
    ...(stairs === undefined ? {} : { stairs }),
  };
}

/**
 * A soma das peças **no serviço**, por chave do serviço — o que `comAsPecas` recebe.
 *
 * Vazio quando a tipologia não tem peça importada: o serviço resolvido passa intacto.
 */
export function pecasDosServicos(
  quantidades: QuantidadesPorPeca | undefined,
): Readonly<Record<string, number>> {
  return quantidades === undefined ? {} : somarPorPeca(quantidades).servicos;
}

/**
 * O número do serviço, com a parte que veio pronta das peças importadas ao lado.
 *
 * `fromPieces` é a soma das peças **neste serviço**, na unidade dele (RN-388). A tela o
 * mostra ao lado do medido — "traçado 2,40 + peças 114,08" —, como mostra o traçado ao lado
 * da correção.
 *
 * `baseDoIndice` só existe ao lado de `fromPieces`: é o que o índice de consumo multiplica.
 * Não é o medido, e a diferença é a RN-384 — o calculado mede só as peças que **não** vieram
 * da planilha, porque a importada já traz cada item pronto. Multiplicar o medido inteiro
 * contaria o concreto do B24 duas vezes: uma pela planilha, outra pelo índice.
 */
export type ServicoComPecas = ResolvedServiceQuantity & {
  readonly fromPieces?: number;
  readonly baseDoIndice?: number | undefined;
};

/**
 * O serviço resolvido, com as peças importadas somadas (RN-384, RN-388).
 *
 * - **Sem decisão, soma.** O medido é o traçado das peças **não** importadas mais a soma das
 *   importadas: as duas partes não se sobrepõem, porque a peça de nome importado já saiu do
 *   desenho (`semPecasImportadas`). Serviço que o desenho não mede — ou que ainda pede o
 *   elemento — passa a ter o número das peças: a planilha diz quanto, e conta já (D3). O índice
 *   multiplica só o traçado.
 * - **Quem digitou manda.** A correção do desenho e o número do serviço digitado passam a ser
 *   o número **inteiro** da tipologia, e as peças não somam por cima — ficam ao lado, para a
 *   tela dizer de quanto o digitado discorda. É a regra do item (`MANUAL` vence `PECAS`). O
 *   índice multiplica o que o digitado tem além das peças, e nunca menos que zero: digitar o
 *   mesmo total que a soma dava não muda conta nenhuma.
 * - **A unidade do serviço.** Quando ela é a da medida, o número na unidade é o medido, e
 *   acompanha. Quando diverge (o pilar contado em "un"), o número na unidade é outro, contado
 *   ou digitado, e as peças não mexem nele.
 */
export function comAsPecas(
  resolvido: ResolvedServiceQuantity,
  dasPecas: number | undefined,
): ServicoComPecas {
  if (dasPecas === undefined) return resolvido;
  if (resolvido.measuredOrigin === 'CORRIGIDO' || resolvido.measuredOrigin === 'DIGITADO') {
    return {
      ...resolvido,
      fromPieces: dasPecas,
      baseDoIndice: Math.max(0, (resolvido.measured ?? 0) - dasPecas),
    };
  }
  const measured = (resolvido.measured ?? 0) + dasPecas;
  return {
    ...resolvido,
    measured,
    fromPieces: dasPecas,
    baseDoIndice: resolvido.measured,
    ...(resolvido.unitsDiverge
      ? {}
      : { inServiceUnit: measured, inServiceUnitOrigin: 'MEDIDO' as const }),
  };
}

/**
 * O que o índice de consumo multiplica neste serviço — o que `TakeoffQuantities.measured`
 * espera. Sem peça importada, é o medido; com ela, a `baseDoIndice` (RN-384).
 */
export function baseDoIndice(resolvido: ServicoComPecas): number | undefined {
  return resolvido.fromPieces === undefined ? resolvido.measured : resolvido.baseDoIndice;
}
